import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  RECRUITMENT_EVENT_CREATION_EMAIL_PREVIEW_QUERY,
  type EmailPreview,
  type RecruitmentEventCreationEmailPreviewInput,
  type RecruitmentEventCreationEmailPreviewQueryResponse,
  type RecruitmentEventCreationEmailPreviewQueryVariables
} from "../_queries/emailPreviewQueries";

export function useRecruitmentEventCreationEmailPreview() {
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const [fetchPreview] = useLazyQuery<
    RecruitmentEventCreationEmailPreviewQueryResponse,
    RecruitmentEventCreationEmailPreviewQueryVariables
  >(RECRUITMENT_EVENT_CREATION_EMAIL_PREVIEW_QUERY, { fetchPolicy: "network-only" });

  const resetPreview = useCallback(() => {
    activeRequestIdRef.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  const loadPreview = useCallback(
    async (input: RecruitmentEventCreationEmailPreviewInput) => {
      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;

      setLoading(true);
      setPreview(null);
      setError(null);

      const result = await fetchPreview({
        variables: { input }
      });

      if (requestId !== activeRequestIdRef.current) {
        return result;
      }

      if (result.error) {
        setError(result.error);
        setPreview(null);
      } else {
        setPreview(result.data?.recruitmentEventCreationEmailPreview ?? null);
        setError(null);
      }

      setLoading(false);
      return result;
    },
    [fetchPreview]
  );

  return {
    loadPreview,
    resetPreview,
    preview,
    loading,
    error
  };
}
